/**
 * Quiz collections listing page.
 * Supports quick management operations such as create, publish, share, play, and analytics navigation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Form,
  Tooltip,
  Typography,
  Avatar,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  UserOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import quizCollectionsService from '../../services/quizCollectionsService';
import { getFileUrl } from '../../utils/urlUtils';
import styles from './Quiz.module.css';

const { Search } = Input;
const { Title, Text } = Typography;

/**
 * Main quiz collections management page.
 *
 * @returns {JSX.Element} Quiz collections table and management modals.
 */
const QuizCollectionsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCollection, setQrCollection] = useState(null);

  const fetchCollections = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await quizCollectionsService.getQuizCollections({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
      });
      setCollections(response.items || []);
      setPagination(prev => ({
        ...prev,
        current: response.page,
        total: response.totalCount,
      }));
    } catch (error) {
      message.error(t('quiz.fetchError', 'Failed to load quiz collections'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, t]);

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCollections(1, value);
  };

  const handleTableChange = (paginationConfig) => {
    fetchCollections(paginationConfig.current);
  };

  const handleCreate = () => {
    setEditingCollection(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (collection) => {
    navigate(`/quiz-collections/${collection.id}`);
  };

  const handleQuickEdit = (collection) => {
    setEditingCollection(collection);
    form.setFieldsValue({
      name: collection.name,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingCollection) {
        await quizCollectionsService.updateQuizCollection(editingCollection.id, values);
        message.success(t('quiz.updateSuccess', 'Quiz collection updated successfully'));
      } else {
        const newCollection = await quizCollectionsService.createQuizCollection(values);
        message.success(t('quiz.createSuccess', 'Quiz collection created successfully'));
        // Navigate to editor for new collections
        navigate(`/quiz-collections/${newCollection.id}`);
        return;
      }

      setModalVisible(false);
      fetchCollections(pagination.current);
    } catch (error) {
      if (error.errorFields) return; // Form validation error
      message.error(t('quiz.saveError', 'Failed to save quiz collection'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (collection) => {
    try {
      await quizCollectionsService.deleteQuizCollection(collection.id);
      message.success(t('quiz.deleteSuccess', 'Quiz collection deleted successfully'));
      fetchCollections(pagination.current);
    } catch (error) {
      message.error(t('quiz.deleteError', 'Failed to delete quiz collection'));
    }
  };

  const handleTogglePublish = async (collection) => {
    try {
      if (collection.isPublished) {
        await quizCollectionsService.unpublishQuizCollection(collection.id);
        message.success(t('quiz.unpublishSuccess', 'Quiz collection unpublished'));
      } else {
        const result = await quizCollectionsService.publishQuizCollection(collection.id);
        if (result.validation && !result.validation.isValid) {
          message.warning(
            t('quiz.publishFailed', 'Cannot publish: ') + 
            result.validation.errors.join(', ')
          );
          return;
        }
        message.success(t('quiz.publishSuccess', 'Quiz collection published'));
      }
      fetchCollections(pagination.current);
    } catch (error) {
      message.error(t('quiz.togglePublishError', 'Failed to update quiz collection status'));
    }
  };

  const handleCopyLink = (collection) => {
    const url = `${window.location.origin}/quiz/${collection.id}`;
    navigator.clipboard.writeText(url);
    message.success(t('quiz.linkCopied', 'Quiz link copied to clipboard'));
  };

  const handleOpenQuiz = (collection) => {
    window.open(`/quiz/${collection.id}`, '_blank');
  };

  const handleShowQrCode = (collection) => {
    setQrCollection(collection);
    setQrModalVisible(true);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('quiz-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${qrCollection?.name || 'quiz'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleRefresh = () => {
    fetchCollections(pagination.current, searchText);
  };

  const columns = [
    {
      title: t('quiz.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span className={styles.collectionName} onClick={() => handleEdit(record)}>
          {text}
        </span>
      ),
    },
    {
      title: t('quiz.questions', 'Questions'),
      key: 'questions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <span className={styles.questionCount}>
            {record.publishedQuestionCount}/{record.questionCount}
          </span>
        </Space>
      ),
    },
    {
      title: t('quiz.topics', 'Topics'),
      dataIndex: 'topicCount',
      key: 'topicCount',
      width: 80,
      render: (count) => (
        <span className={styles.topicCount}>{count}</span>
      ),
    },
    {
      title: t('quiz.status', 'Status'),
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 120,
      render: (isPublished) => (
        <Tag
          color={isPublished ? 'green' : 'default'}
          icon={isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          className={styles.statusTag}
        >
          {isPublished ? t('quiz.published', 'Published') : t('quiz.draft', 'Draft')}
        </Tag>
      ),
    },
    {
      title: t('quiz.owner', 'Owner'),
      key: 'owner',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Avatar 
            size="small" 
            src={getFileUrl(record.ownerProfileImageUrl)}
            icon={!record.ownerProfileImageUrl && <UserOutlined />}
          />
          <span>{record.ownerName || t('common.unknown', 'Unknown')}</span>
        </Space>
      ),
    },
    {
      title: t('quiz.actions', 'Actions'),
      key: 'actions',
      width: 340,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('quiz.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.isPublished ? t('quiz.unpublish', 'Unpublish') : t('quiz.publish', 'Publish')}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleTogglePublish(record)}
            />
          </Tooltip>
          {record.isPublished && (
            <Tooltip title={t('quiz.copyLink', 'Copy Link')}>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('quiz.qrCode', 'QR Code')}>
              <Button
                type="text"
                icon={<QrcodeOutlined />}
                onClick={() => handleShowQrCode(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('quiz.playQuiz', 'Play Quiz')}>
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handleOpenQuiz(record)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('quiz.statistics.title', 'Statistics')}>
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/quiz-collections/${record.id}/statistics`)}
            />
          </Tooltip>
          <Tooltip title={t('quiz.attempts.title', 'Attempts')}>
            <Button
              type="text"
              icon={<UnorderedListOutlined />}
              onClick={() => navigate(`/quiz-collections/${record.id}/attempts`)}
            />
          </Tooltip>
          <Popconfirm
            title={t('quiz.deleteConfirm', 'Delete this quiz collection?')}
            description={t('quiz.deleteDescription', 'This will permanently delete the collection and all questions.')}
            onConfirm={() => handleDelete(record)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('quiz.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.quizContainer}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <QuestionCircleOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>
              {t('quiz.title', 'Quiz Collections')}
            </Title>
          </div>
          <Text type="secondary">
            {t('quiz.description', 'Create and manage quiz collections for training and testing')}
          </Text>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder={t('quiz.searchPlaceholder', 'Search quiz collections...')}
              onSearch={handleSearch}
              allowClear
              style={{ width: 300 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('quiz.createNew', 'Create Quiz Collection')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={collections}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => t('quiz.totalItems', `Total: ${total} collections`, { total }),
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <div className={styles.emptyState}>
                <p>{t('quiz.noCollections', 'No quiz collections found')}</p>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  {t('quiz.createFirst', 'Create your first quiz collection')}
                </Button>
              </div>
            ),
          }}
        />

        <Modal
          title={editingCollection ? t('quiz.editCollection', 'Edit Quiz Collection') : t('quiz.createCollection', 'Create Quiz Collection')}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={() => setModalVisible(false)}
          confirmLoading={submitting}
          okText={editingCollection ? t('common.save', 'Save') : t('common.create', 'Create')}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label={t('quiz.collectionName', 'Collection Name')}
              rules={[
                { required: true, message: t('quiz.nameRequired', 'Please enter a collection name') },
                { max: 255, message: t('quiz.nameTooLong', 'Name is too long (max 255 characters)') },
              ]}
            >
              <Input placeholder={t('quiz.namePlaceholder', 'Enter collection name...')} />
            </Form.Item>

            {!editingCollection && (
              <Text type="secondary">
                {t('quiz.createNote', 'After creating, you will be taken to the editor to add questions.')}
              </Text>
            )}
          </Form>
        </Modal>

        <Modal
          title={t('quiz.qrCode', 'QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
              {t('quiz.downloadQrCode', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          centered
        >
          {qrCollection && (
            <div className={styles.qrCodeContainer}>
              <QRCodeSVG
                id="quiz-qr-code"
                value={`${window.location.origin}/quiz/${qrCollection.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <p className={styles.qrCodeCollectionName}>{qrCollection.name}</p>
              <p className={styles.qrCodeLink}>{`${window.location.origin}/quiz/${qrCollection.id}`}</p>
            </div>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default QuizCollectionsList;
